import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { adminUpdateStudentPhase, getPhaseData } from '../../lib/api';
import type { CoursePhase, PhaseStudent } from '../../lib/api';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

const PHASES: CoursePhase[] = ['First 5 Days', 'Mid Course', 'Last 10 Days'];

const PHASE_CFG: Record<CoursePhase, {
  label: string; icon: string;
  gradient: string; lightBg: string; color: string; border: string; dot: string; ring: string;
}> = {
  'First 5 Days': {
    label: 'First 5 Days',  icon: '🌱',
    gradient: 'linear-gradient(135deg,#78350f,#d97706)',
    lightBg: '#fffbeb', color: '#92400e', border: '#fde68a', dot: '#f59e0b', ring: '#fef3c7',
  },
  'Mid Course': {
    label: 'Mid Course',    icon: '⚡',
    gradient: 'linear-gradient(135deg,#4c1d95,#7c3aed)',
    lightBg: '#faf5ff', color: '#4c1d95', border: '#ddd6fe', dot: '#7c3aed', ring: '#ede9fe',
  },
  'Last 10 Days': {
    label: 'Last 10 Days',  icon: '🏁',
    gradient: 'linear-gradient(135deg,#14532d,#16a34a)',
    lightBg: '#f0fdf4', color: '#14532d', border: '#bbf7d0', dot: '#22c55e', ring: '#dcfce7',
  },
};

// ── INLINE STYLES ─────────────────────────────────────────────────────────────

const CSS = `
@keyframes ptCardIn {
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0)   scale(1);    }
}
@keyframes ptSlideCol {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes ptPulse {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.08); }
}
.pt-card-anim { animation: ptCardIn 0.3s cubic-bezier(.25,.46,.45,.94) both; }
.pt-col-anim  { animation: ptSlideCol 0.35s ease both; }
.pt-badge-pulse { animation: ptPulse 1.8s ease-in-out infinite; }
`;

// ── STUDENT CARD ──────────────────────────────────────────────────────────────

const StudentCard: React.FC<{
  student: PhaseStudent;
  currentPhase: CoursePhase;
  onPhaseChange: (studentId: string, phase: CoursePhase) => void;
  moving: boolean;
  delay?: number;
}> = ({ student, currentPhase, onPhaseChange, moving, delay = 0 }) => {
  const cfg = PHASE_CFG[currentPhase];
  const initials = student.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="pt-card-anim bg-white rounded-xl border shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
      style={{ borderColor: cfg.border, animationDelay: `${delay}ms` }}>

      {/* Top accent strip */}
      <div className="h-1" style={{ background: cfg.gradient }} />

      {/* Card body */}
      <div className="p-3.5">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0 shadow-sm"
            style={{ background: cfg.gradient, color: '#fff' }}>
            {initials}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-gray-900 truncate leading-tight">{student.name}</p>
            <p className="text-xs text-gray-500 truncate mt-0.5">{student.course}</p>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{student.studentId}</span>
              {student.mentorName && (
                <span className="text-[10px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                  👤 {student.mentorName}
                </span>
              )}
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{ background: cfg.ring, color: cfg.color }}>
                {student.classCount} classes
              </span>
            </div>
          </div>
        </div>

        {/* Move to buttons */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Move to phase:</p>
          <div className="flex gap-1.5 flex-wrap">
            {PHASES.filter(p => p !== currentPhase).map(p => {
              const pc = PHASE_CFG[p];
              return (
                <button key={p} disabled={moving}
                  onClick={() => onPhaseChange(student.studentId, p)}
                  className="text-[11px] px-2.5 py-1 rounded-lg font-bold border-2 transition-all disabled:opacity-40 hover:opacity-85 active:scale-95"
                  style={{ background: pc.lightBg, color: pc.color, borderColor: pc.border }}>
                  {pc.icon} {pc.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {moving && (
        <div className="border-t px-3 py-2 flex items-center gap-2" style={{ borderColor: cfg.border, background: cfg.ring }}>
          <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: cfg.dot }} />
          <span className="text-[11px] font-medium" style={{ color: cfg.color }}>Moving…</span>
        </div>
      )}
    </div>
  );
};

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────

const AdminPhaseTracker: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [students, setStudents] = useState<PhaseStudent[]>([]);
  const [loading, setLoading]   = useState(true);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [search, setSearch]     = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    if (styleRef.current) return;
    const el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
    styleRef.current = el;
    return () => { el.remove(); styleRef.current = null; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPhaseData(adminPassword);
      setStudents(res.students);
    } catch (err: any) {
      toast.error('Failed to load phase data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const handlePhaseChange = async (studentId: string, phase: CoursePhase) => {
    setMovingId(studentId);
    try {
      await adminUpdateStudentPhase(adminPassword, studentId, phase);
      setStudents(prev => prev.map(s => s.studentId === studentId ? { ...s, phase } : s));
      toast.success('Phase updated');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setMovingId(null);
    }
  };

  const courses = Array.from(new Set(students.map(s => s.course).filter(Boolean))).sort();

  const filtered = students.filter(s => {
    if (filterCourse && s.course !== filterCourse) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.course || '').toLowerCase().includes(q) ||
      (s.mentorName || '').toLowerCase().includes(q) ||
      (s.studentId || '').toLowerCase().includes(q)
    );
  });

  const byPhase = (phase: CoursePhase) => filtered.filter(s => (s.phase || 'First 5 Days') === phase);
  const hasFilters = search || filterCourse;

  return (
    <TrackerLayout>
      <div className="flex flex-col" style={{ height: 'calc(100vh - 65px)' }}>

        {/* ── Header ── */}
        <div className="flex-shrink-0 mb-4">
          {/* Title row */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-black" style={{ color: '#2b2240' }}>Phase Tracker</h1>
              <p className="text-xs text-gray-400 mt-0.5">
                {students.length} active students · drag students between phases
              </p>
            </div>
            <button onClick={load}
              className="px-3 py-2 rounded-lg text-xs font-semibold border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
              ↻ Refresh
            </button>
          </div>

          {/* Filter row */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input type="text" placeholder="Search name, course, mentor…"
                value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-xl text-xs focus:outline-none focus:border-yellow-400 transition-colors bg-white" />
            </div>

            {/* Course filter */}
            <select value={filterCourse} onChange={e => setFilterCourse(e.target.value)}
              className="text-xs border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 min-w-[120px]">
              <option value="">All Courses</option>
              {courses.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            {hasFilters && (
              <button onClick={() => { setSearch(''); setFilterCourse(''); }}
                className="text-xs font-semibold px-3 py-2 rounded-xl border border-red-200 text-red-500 bg-red-50 hover:bg-red-100 transition-colors">
                Clear
              </button>
            )}

            <span className="ml-auto text-xs text-gray-400">{filtered.length} / {students.length} shown</span>
          </div>
        </div>

        {/* ── Kanban ── */}
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3">
            <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading phase data…</span>
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-3 gap-4">
            {PHASES.map((phase, colIdx) => {
              const cfg  = PHASE_CFG[phase];
              const cols = byPhase(phase);
              return (
                <div key={phase}
                  className="pt-col-anim flex flex-col rounded-2xl overflow-hidden shadow-sm border"
                  style={{ borderColor: cfg.border, animationDelay: `${colIdx * 80}ms` }}>

                  {/* Column header */}
                  <div className="flex-shrink-0 px-4 py-3" style={{ background: cfg.gradient }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{cfg.icon}</span>
                        <span className="font-bold text-sm text-white">{cfg.label}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-black px-2 py-0.5 rounded-full text-white"
                          style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
                          {cols.length}
                        </span>
                        {cols.length > 0 && (
                          <span className="pt-badge-pulse text-xs opacity-80">
                            <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0"/>
                            </svg>
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Phase stats bar */}
                    <div className="mt-2 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }}>
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${students.length ? Math.round((cols.length / students.length) * 100) : 0}%`, background: 'rgba(255,255,255,0.7)' }} />
                    </div>
                    <p className="text-[10px] mt-0.5 text-white/60">
                      {students.length ? Math.round((cols.length / students.length) * 100) : 0}% of all students
                    </p>
                  </div>

                  {/* Cards scroll area */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-2.5" style={{ background: cfg.lightBg }}>
                    {cols.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="text-3xl mb-2 opacity-40">{cfg.icon}</div>
                        <p className="text-xs font-medium" style={{ color: cfg.color, opacity: 0.5 }}>
                          No students in this phase
                        </p>
                      </div>
                    ) : (
                      cols.map((s, cardIdx) => (
                        <StudentCard
                          key={s.studentId}
                          student={s}
                          currentPhase={phase}
                          onPhaseChange={handlePhaseChange}
                          moving={movingId === s.studentId}
                          delay={cardIdx * 40}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default AdminPhaseTracker;
